import { useState, type ComponentProps, type ReactNode } from "react";
import { CatalogPicker } from "@core/components/CatalogPicker";
import { LongText } from "@core/components/LongText";
import type { LocalizedText } from "@core/api/types";
import { BudgetBar, Chips, List, ListHeading, ListRow } from "@core/components/List";
import { Sheet } from "@core/components/Sheet";
import { Stepper } from "@core/components/Stepper";
import { Value } from "@core/components/Value";
import { alreadyTaken, page, resourceLabel, skillCategories, useSm, type Allocation, type BudgetKeys, type CatalogEntry, type EntryDetails, type MasteryOffer } from "./view";
import { ElaboratedRow } from "./elaboration";

/**
 * Choosing a new mastery: first one of the skills the engine offers, then one of its masteries. Those that cannot be taken stay
 * visible, greyed out with the reason the engine gives (GRW p. 92).
 */
export function MasteryOfferPicker(props: {
  title: string;
  offers: MasteryOffer[];
  /** Which skills are listed, e.g. those with enough points. */
  intro: ReactNode;
  empty: string;
  hint?: ReactNode;
  onPick: (mastery: { id: string; name: string }, skill: { id: string; name: string }) => void;
  onClose: () => void;
}) {
  const { view } = useSm();
  const [skill, setSkill] = useState<string | null>(null);
  const offer = props.offers.find((o) => o.skill === skill);
  const skillName = (id: string) => view.skills.find((s) => s.id === id)?.name ?? id;

  if (!offer) {
    return (
      <Sheet title={props.title} onClose={props.onClose}>
        <div className="stack small-gap">
          <p className="muted small">{props.intro}</p>
          <List aria-label={`Fertigkeiten: ${props.title}`}>
            {props.offers.length === 0 && <li className="list-empty">{props.empty}</li>}
            {props.offers.map((o) => {
              const points = view.skills.find((s) => s.id === o.skill)?.points;
              const open = o.masteries.filter((m) => !m.blocked).length;
              return (
                <ListRow
                  key={o.skill}
                  title={skillName(o.skill)}
                  subtitle={
                    <>
                      {points && (
                        <>
                          <Value valueKey={points} /> Punkte ·{" "}
                        </>
                      )}
                      {open === 0 ? "keine Meisterschaft verfügbar" : `${open} verfügbar`}
                    </>
                  }
                  actions={
                    <button type="button" className="button secondary" onClick={() => setSkill(o.skill)}>
                      Wählen
                    </button>
                  }
                />
              );
            })}
          </List>
        </div>
      </Sheet>
    );
  }

  const blocked = new Map(offer.masteries.map((m) => [m.id, m.blocked]));
  const name = skillName(offer.skill);
  return (
    <CatalogPicker
      type="masteries"
      title={`Meisterschaft in ${name}`}
      filter={{ skill: offer.skill }}
      hint={props.hint}
      before={
        <button type="button" className="link" onClick={() => setSkill(null)}>
          ← Andere Fertigkeit
        </button>
      }
      unavailable={(item) => (blocked.has(item.id) ? blocked.get(item.id) : "nicht verfügbar")}
      onClose={props.onClose}
      onPick={(item) => {
        props.onClose();
        props.onPick({ id: item.id, name: (item.details as { name?: string } | undefined)?.name ?? item.label.de }, { id: offer.skill, name });
      }}
    />
  );
}

/** Asks for a short text, e.g. the name of a further instance of a resource; empty submits undefined. */
export function NameSheet(props: { title: string; hint?: ReactNode; placeholder?: string; initial?: string; submitLabel?: string; onSubmit: (name: string | undefined) => void; onClose: () => void }) {
  const [name, setName] = useState(props.initial ?? "");
  const submit = () => props.onSubmit(name.trim() || undefined);
  return (
    <Sheet
      title={props.title}
      onClose={props.onClose}
      footer={
        <>
          <button type="button" className="button secondary" onClick={props.onClose}>
            Abbrechen
          </button>
          <button type="button" className="button primary" onClick={submit}>
            {props.submitLabel ?? "Übernehmen"}
          </button>
        </>
      }
    >
      <div className="stack small-gap">
        {props.hint && <p className="muted small">{props.hint}</p>}
        <input autoFocus value={name} placeholder={props.placeholder} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} aria-label={props.title} />
      </div>
    </Sheet>
  );
}

/**
 * Title and hints when a further instance of a resource gets its name. A resource with a parameter names it, e.g. the organisation
 * of a Rang; without an answer that stays an open choice. Otherwise the name is optional and the points add to the existing instance.
 */
export function instanceNaming(resource: string, parameter?: string | null) {
  return parameter
    ? {
        title: `${resource}: ${parameter}`,
        hint: `${resource} wird je ${parameter} geführt; die Angabe benennt die Instanz. Ohne Angabe bleibt sie eine offene Wahl (GRW S. 79).`,
        placeholder: parameter,
      }
    : {
        title: `${resource}: getrennt führen?`,
        hint: "Ein Name legt eine eigene Instanz an, etwa ein zweites Relikt. Ohne Namen zählen die Punkte zur vorhandenen Ressource (GRW S. 55).",
        placeholder: "Name der Instanz (optional)",
      };
}

/** Asks for a number of points; what they cost shows the preview of the purchase. */
export function AmountSheet(props: { title: string; hint?: ReactNode; min?: number; max?: number; onSubmit: (amount: number) => void; onClose: () => void }) {
  const [amount, setAmount] = useState(props.min ?? 1);
  return (
    <Sheet
      title={props.title}
      onClose={props.onClose}
      footer={
        <>
          <button type="button" className="button secondary" onClick={props.onClose}>
            Abbrechen
          </button>
          <button type="button" className="button primary" onClick={() => props.onSubmit(amount)}>
            Kaufen
          </button>
        </>
      }
    >
      <div className="stack small-gap">
        {props.hint && <p className="muted small">{props.hint}</p>}
        <div className="row spread">
          <span>Punkte</span>
          <Stepper label="Punkte" value={amount} min={props.min ?? 1} max={props.max} onChange={setAmount} />
        </div>
      </div>
    </Sheet>
  );
}

export interface SpellSchoolOption {
  school: string;
  grade: number;
}

/** Asks in which school a spell of several schools is learned; grade and cost depend on the school (GRW S. 25, 87). */
export function SpellSchoolSheet(props: { spell: string; schools: SpellSchoolOption[]; onPick: (option: SpellSchoolOption) => void; onClose: () => void }) {
  const { view } = useSm();
  return (
    <Sheet title={`${props.spell}: Schule`} onClose={props.onClose}>
      <div className="stack small-gap">
        <p className="muted small">Der Zauber gehört zu mehreren Schulen. Grad und Kosten richten sich nach der Schule, in der er gelernt wird.</p>
        <List aria-label="Schulen">
          {props.schools.map((option) => {
            const skill = view.skills.find((s) => s.id === option.school);
            return (
              <ListRow
                key={option.school}
                title={skill?.name ?? option.school}
                subtitle={<>Grad {option.grade}{skill && <> · <Value valueKey={skill.points} /> Punkte in der Schule</>}</>}
                actions={
                  <button type="button" className="button secondary" onClick={() => props.onPick(option)}>
                    Wählen
                  </button>
                }
              />
            );
          })}
        </List>
      </div>
    </Sheet>
  );
}

/** After a pick the cards give way to one row: what was chosen, in short, and a button to open the cards again. */
export function ChosenRow(props: { kicker: string; title: ReactNode; facts?: ReactNode; source?: string; description?: LocalizedText | null; onChange: () => void; changeLabel?: string }) {
  const { language } = useSm();
  return (
    <section className="card chosen-row">
      <span className="mark done" aria-hidden="true">
        ✓
      </span>
      <span className="list-main">
        <span className="muted small">
          {props.kicker}
          {props.source && <> · {props.source}</>}
        </span>
        <span className="list-title">
          <strong>{props.title}</strong>
        </span>
        {props.facts && <span className="list-subtitle">{props.facts}</span>}
        <LongText value={props.description} language={language} />
      </span>
      <button type="button" className="button secondary" onClick={props.onChange}>
        {props.changeLabel ?? "Ändern"}
      </button>
    </section>
  );
}

/** A grid of modules (races, cultures, ancestries, educations) to choose from. */
export function ModuleCards<T extends CatalogEntry>(props: {
  entries: T[];
  selectedId?: string | null;
  /** Ids that fit what was chosen before; they are listed first with a badge. */
  typical?: Set<string>;
  typicalLabel?: string;
  details?: (entry: T) => ReactNode;
  /** 4 when the cards sit under a heading of their own, such as a region, 5 under a sub-heading. */
  headingLevel?: 3 | 4 | 5;
  onPick: (entry: T) => void;
}) {
  const { language } = useSm();
  const Heading = (`h${props.headingLevel ?? 3}`) as "h3" | "h4" | "h5";
  const sorted = props.typical?.size ? [...props.entries].sort((a, b) => Number(props.typical!.has(b.id)) - Number(props.typical!.has(a.id))) : props.entries;
  return (
    <div className="module-grid">
      {sorted.map((entry) => {
        const selected = props.selectedId === entry.id;
        return (
          <article key={entry.id} className={`card module-card${selected ? " selected" : ""}`}>
            <header>
              <Heading>{entry.name}</Heading>
              <span className="source">{page(entry.source)}</span>
            </header>
            {props.typical?.has(entry.id) && <span className="badge typical">{props.typicalLabel ?? "passt"}</span>}
            {entry.summary && <p className="muted small">{entry.summary}</p>}
            <LongText value={entry.description} language={language} />
            {props.details?.(entry)}
            <button type="button" className={`button ${selected ? "secondary" : "primary"}`} disabled={selected} aria-label={`${entry.name} wählen`} onClick={() => props.onPick(entry)}>
              {selected ? "Gewählt" : "Wählen"}
            </button>
          </article>
        );
      })}
    </div>
  );
}

interface AllocationProps {
  /** `frei` for the free points of step 7, otherwise the module step. */
  step: string;
  allocation: Allocation;
  /** The module's printed values while individualising, for comparison. */
  printed?: Allocation | null;
  /** Value keys of the budget as the engine counts it. */
  budget: BudgetKeys;
}

/** Keys of a budget the engine did not define; its values show as missing. */
export const missingBudget: BudgetKeys = { left: "", total: "", spent: "" };

/** Spent and total of a budget as numbers for the bar, with the values for their derivations. */
function useBudget(keys: BudgetKeys) {
  const { budget } = useSm();
  return {
    spent: budget(keys.spent),
    total: budget(keys.total),
    left: Math.max(0, budget(keys.left)),
    spentText: <Value valueKey={keys.spent} />,
    totalText: <Value valueKey={keys.total} />,
  };
}

/** Skill points of one allocation: a budget, the skills with points and a search over all others. */
export function SkillAllocation(props: AllocationProps & { perSkillMax?: number; magicMax?: number; magicCount?: number | null; title?: string }) {
  const { view, apply, value } = useSm();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const skillMax = view.catalog?.limits.skillMax ?? 6;
  const budget = useBudget(props.budget);
  const remaining = budget.left;
  const magicSchools = Object.entries(props.allocation.skills).filter(([id, v]) => v > 0 && view.skills.find((s) => s.id === id)?.category === "magie").length;
  const setPoints = (skill: string, points: number) => apply({ type: "sm.setPoints", payload: { step: props.step, kind: "skill", target: skill, value: points } });
  const searching = query.trim().length > 0 || category !== null || showAll;
  const needle = query.trim().toLowerCase();
  const rows = view.skills.filter((s) => {
    const own = props.allocation.skills[s.id] ?? 0;
    if (!searching) return own !== 0;
    if (category && s.category !== category) return false;
    return !needle || s.name.toLowerCase().includes(needle);
  });

  return (
    <div className="allocation-block">
      <BudgetBar
        label={props.title ?? "Fertigkeitspunkte"}
        used={budget.spent}
        total={budget.total}
        usedText={budget.spentText}
        totalText={budget.totalText}
        hint={perSkillHint(props.perSkillMax, props.magicMax, props.magicCount, skillMax)}
      />
      <div className="row">
        <input type="search" placeholder="Fertigkeit suchen …" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Fertigkeit suchen" className="grow" />
        <button type="button" className="link" onClick={() => setShowAll(!showAll)}>
          {showAll ? "nur vergebene" : "alle zeigen"}
        </button>
      </div>
      {searching && <Chips ariaLabel="Kategorie" allLabel="Alle" options={skillCategories.map((c) => ({ value: c.id, label: c.label }))} value={category} onChange={setCategory} />}
      <List aria-label={props.title ?? "Fertigkeitspunkte"}>
        {rows.length === 0 && <li className="list-empty">{searching ? "Keine Fertigkeit gefunden." : "Noch keine Punkte vergeben. Suche eine Fertigkeit oder zeige alle."}</li>}
        {rows.map((s) => {
          const own = props.allocation.skills[s.id] ?? 0;
          const total = value(s.points)?.number ?? 0;
          const others = total - own;
          const printed = props.printed ? (props.printed.skills[s.id] ?? 0) : null;
          const isMagic = s.category === "magie";
          const perSkill = isMagic ? (props.magicMax ?? props.perSkillMax) : props.perSkillMax;
          const magicBlocked = isMagic && own === 0 && props.magicCount != null && magicSchools >= props.magicCount;
          const max = Math.min(perSkill ?? skillMax, skillMax - others, own + remaining);
          return (
            <ListRow
              key={s.id}
              title={s.name}
              subtitle={
                <>
                  {others !== 0 && <>{others} aus anderen Quellen · </>}
                  gesamt {total} von {skillMax}
                  {printed != null && printed !== own && <> · Modul: {printed}</>}
                </>
              }
              badges={isMagic ? <span className="badge">Magie</span> : s.category === "kampf" ? <span className="badge">Kampf</span> : undefined}
              value={<Stepper label={s.name} value={own} min={Math.min(0, own)} max={magicBlocked ? own : Math.max(own, max)} onChange={(v) => setPoints(s.id, v)} />}
            />
          );
        })}
      </List>
    </div>
  );
}

function perSkillHint(perSkillMax: number | undefined, magicMax: number | undefined, magicCount: number | null | undefined, skillMax: number): string {
  const parts: string[] = [];
  if (perSkillMax != null) parts.push(`je Fertigkeit höchstens ${perSkillMax}`);
  if (magicMax != null) parts.push(magicMax === 0 ? "keine Magieschulen" : `Magieschulen höchstens ${magicMax}`);
  if (magicCount != null) parts.push(`höchstens ${magicCount} Magieschule${magicCount === 1 ? "" : "n"}`);
  parts.push(`insgesamt höchstens ${skillMax} je Fertigkeit`);
  return parts.join(", ");
}

/**
 * Resource points of one allocation over all resources; core resources may go below 0 in step 7 and in a module step whose data allow it,
 * only step 4 in the GRW (SM-ABST-01, SM-STAE-03). Elsewhere only a value a module prints may stay below 0.
 */
export function ResourceAllocation(props: AllocationProps & { title?: string; notes?: boolean }) {
  const { view, apply, value } = useSm();
  const [naming, setNaming] = useState<string | null>(null);
  const [organising, setOrganising] = useState<{ id: string; name: string; parameter: string } | null>(null);
  const catalog = view.catalog?.resources ?? [];
  const stepLimits = view.catalog?.steps[props.step];
  const negativeAllowed = stepLimits ? stepLimits.negativeCoreResources === true : true;
  const budget = useBudget(props.budget);
  const remaining = budget.left;
  const setPoints = (key: string, points: number) => apply({ type: "sm.setPoints", payload: { step: props.step, kind: "resource", target: key, value: points } });
  const keys = [...new Set([...catalog.map((r) => r.id), ...Object.keys(props.allocation.resources), ...view.resources.map((r) => r.key)])];
  const ordered = keys
    .map((key) => ({ key, def: catalog.find((r) => r.id === key.split("#")[0]) }))
    .sort((a, b) => Number(b.def?.core ?? false) - Number(a.def?.core ?? false));
  return (
    <div className="allocation-block">
      <BudgetBar
        label={props.title ?? "Ressourcenpunkte"}
        used={budget.spent}
        total={budget.total}
        usedText={budget.spentText}
        totalText={budget.totalText}
        hint={
          negativeAllowed
            ? "Kernressourcen dürfen bis zu ihrem Mindestwert gesenkt werden; jede gesenkte Stufe gibt einen Punkt frei. Ein negativer Wert, den kein Modul vorgibt, braucht die Zustimmung des Spielleiters."
            : "Negative Kernressourcen gibt es in diesem Schritt nicht, erst bei den freien Punkten in Schritt 7."
        } />
      <List aria-label={props.title ?? "Ressourcenpunkte"}>
        {ordered.map(({ key, def }) => {
          const own = props.allocation.resources[key] ?? 0;
          const held = view.resources.find((r) => r.key === key);
          const totalKey = held?.value;
          const total = totalKey ? (value(totalKey)?.number ?? 0) : own;
          const others = total - own;
          const printed = props.printed ? (props.printed.resources[key] ?? 0) : null;
          // The limits apply to the total; every further point costs one, whether it is spent or a lowered level taken back.
          const min = Math.min(own, def?.core ? (negativeAllowed ? def.min - others : Math.min(0, printed ?? 0)) : 0);
          const max = Math.max(own, Math.min((def?.max ?? 6) - others, own + remaining));
          // Points of a resource held per organisation go to a named instance; the unnamed one only gathers points without a name.
          const askFirst = def?.parameter && !key.includes("#") && own === 0 && total === 0 ? { id: key, name: def.name, parameter: def.parameter } : null;
          const row: ComponentProps<typeof ListRow> = {
            title: resourceLabel(view, key),
            badges: def?.core ? <span className="badge">Kernressource</span> : undefined,
            subtitle: (
              <>
                gesamt {total}
                {printed != null && printed !== own && <> · Modul: {printed}</>}
                {def?.summary && <> · {def.summary}</>}
              </>
            ),
            value: askFirst ? (
              <button type="button" className="button secondary small" disabled={remaining <= 0} onClick={() => setOrganising(askFirst)}>
                + {askFirst.parameter}
              </button>
            ) : (
              <Stepper label={resourceLabel(view, key)} value={own} min={min} max={max} onChange={(v) => setPoints(key, v)} />
            ),
          };
          return props.notes && held && total !== 0 ? (
            <ElaboratedRow
              key={key}
              {...row}
              subject={resourceLabel(view, key)}
              elaboration={held}
              onSave={({ note, details }) => apply({ type: "sm.setResourceNote", payload: { key, text: note, details } })}
            />
          ) : (
            <ListRow key={key} {...row} />
          );
        })}
      </List>
      {organising && (
        <NameSheet
          {...instanceNaming(organising.name, organising.parameter)}
          submitLabel="Anlegen"
          onClose={() => setOrganising(null)}
          onSubmit={(name) => {
            setOrganising(null);
            void setPoints(name ? `${organising.id}#${name}` : organising.id, 1);
          }}
        />
      )}
      {remaining > 0 && catalog.some((r) => r.multipleInstances) && (
        <div className="actions">
          <button type="button" className="link" onClick={() => setNaming("")}>
            Weitere Instanz einer Ressource anlegen (z. B. ein zweites Relikt)
          </button>
        </div>
      )}
      {naming !== null && (
        <InstancePicker
          onClose={() => setNaming(null)}
          onPick={(id, name) => {
            setNaming(null);
            void setPoints(`${id}#${name}`, 1);
          }}
        />
      )}
    </div>
  );
}

/** Picks a resource that may be held several times and names the new instance. */
function InstancePicker(props: { onPick: (id: string, name: string) => void; onClose: () => void }) {
  const { view } = useSm();
  const [resource, setResource] = useState<{ id: string; name: string; parameter?: string | null } | null>(null);
  const options = (view.catalog?.resources ?? []).filter((r) => r.multipleInstances);
  if (!resource) {
    return (
      <Sheet title="Ressource" onClose={props.onClose}>
        <p className="muted small">Diese Ressourcen können mehrfach getrennt geführt werden (GRW S. 55).</p>
        <List>
          {options.map((r) => (
            <ListRow key={r.id} title={r.name} subtitle={r.summary ?? undefined} onClick={() => setResource({ id: r.id, name: r.name, parameter: r.parameter })} />
          ))}
        </List>
      </Sheet>
    );
  }
  return (
    <NameSheet
      title={`${resource.name}: ${resource.parameter ?? "Name der Instanz"}`}
      hint={resource.parameter ? `${resource.name} wird je ${resource.parameter} geführt; die Angabe benennt die Instanz (GRW S. 79).` : "Ein Name, unter dem diese Instanz getrennt geführt wird, etwa „Erbstück“ für ein Relikt."}
      placeholder={resource.parameter ?? "Name"}
      submitLabel="Anlegen"
      onClose={props.onClose}
      onSubmit={(name) => name && props.onPick(resource.id, name)}
    />
  );
}

/** Strengths of one allocation, paid with strength points equal to their level. */
export function StrengthAllocation(props: AllocationProps & { title?: string }) {
  const { view, apply } = useSm();
  const [picking, setPicking] = useState(false);
  const levelOf = (id: string) => view.strengths.find((s) => s.id === id)?.level ?? 1;
  const budget = useBudget(props.budget);
  const elsewhere = view.strengths.filter((s) => !props.allocation.strengths.some((o) => o.id === s.id && (o.param ?? null) === (s.param ?? null)));
  return (
    <div className="allocation-block">
      <BudgetBar
        label={props.title ?? "Stärkepunkte"}
        used={budget.spent}
        total={budget.total}
        usedText={budget.spentText}
        totalText={budget.totalText}
        hint="Eine Stärke kostet so viele Punkte, wie ihre Stufe angibt."
      />
      <List aria-label={props.title ?? "Stärkepunkte"}>
        {props.allocation.strengths.length === 0 && <li className="list-empty">Noch keine Stärke gewählt.</li>}
        {props.allocation.strengths.map((s, index) => {
          const def = view.strengths.find((x) => x.id === s.id);
          return (
            <ListRow
              key={`${s.id}-${index}`}
              title={def?.name ?? s.id}
              badges={s.param ? <span className="badge">{s.param}</span> : undefined}
              subtitle={def?.summary ?? undefined}
              value={`${levelOf(s.id)} ${levelOf(s.id) === 1 ? "Punkt" : "Punkte"}`}
              actions={
                <button type="button" className="link danger" onClick={() => void apply({ type: "sm.removeStrength", payload: { step: props.step, index } })}>
                  Entfernen
                </button>
              }
            />
          );
        })}
      </List>
      {elsewhere.length > 0 && (
        <p className="muted small">
          Bereits vorhanden: {elsewhere.map((s) => `${s.name}${s.param ? ` (${s.param})` : ""} – ${[...new Set(s.origins)].join(", ")}`).join("; ")}
        </p>
      )}
      {budget.left <= 0 && (
        <p className="muted small">Alle Stärkepunkte sind verteilt; wählbar bleiben Stärken der Stufe 0. Zum Tauschen erst eine Stärke entfernen.</p>
      )}
      <div className="actions">
        <button type="button" className="button secondary" onClick={() => setPicking(true)}>
          + Stärke wählen
        </button>
      </div>
      {picking && (
        <CatalogPicker
          type="strengths"
          title="Stärke wählen"
          hint="Der Wert rechts ist der Preis in Stärkepunkten. Stärken mit dem Hinweis „nur bei der Erschaffung“ gibt es später nicht mehr (GRW S. 24)."
          unavailable={(item) => {
            const level = (item.details as { level?: number } | null | undefined)?.level ?? 0;
            return alreadyTaken(view, "strengths")(item) ?? (level > budget.left ? `kostet ${level} ${level === 1 ? "Punkt" : "Punkte"}, übrig ${Math.max(0, budget.left)}` : null);
          }}
          onClose={() => setPicking(false)}
          onPick={(item) => {
            setPicking(false);
            // A free-text parameter is asked as an open choice right after the pick.
            void apply({ type: "sm.addStrength", payload: { step: props.step, id: item.id } });
          }}
        />
      )}
    </div>
  );
}

/** Masteries of a module step done freely or individualised; the step allows a fixed number of threshold-1 masteries. */
export function MasteryAllocation(props: AllocationProps & { level: number }) {
  const { view, apply } = useSm();
  const [picking, setPicking] = useState(false);
  const all = view.skills.flatMap((s) => s.masteries);
  const budget = useBudget(props.budget);
  return (
    <div className="allocation-block">
      <BudgetBar
        label="Meisterschaften"
        used={budget.spent}
        total={budget.total}
        usedText={budget.spentText}
        totalText={budget.totalText}
        hint={`Schwelle ${props.level}; die Fertigkeit braucht am Ende der Erschaffung mindestens einen Punkt.`}
      />
      <List aria-label="Meisterschaften">
        {props.allocation.masteries.length === 0 && <li className="list-empty">Noch keine Meisterschaft gewählt.</li>}
        {props.allocation.masteries.map((m, index) => {
          const def = all.find((x) => x.id === m.id);
          const skill = view.skills.find((s) => s.id === m.skill)?.name;
          return (
            <ListRow
              key={`${m.id}-${index}`}
              title={def?.name ?? m.id}
              badges={m.param ? <span className="badge">{m.param}</span> : undefined}
              subtitle={skill ?? def?.summary ?? undefined}
              actions={
                <button type="button" className="link danger" onClick={() => void apply({ type: "sm.removeMastery", payload: { step: props.step, index } })}>
                  Entfernen
                </button>
              }
            />
          );
        })}
      </List>
      {budget.left > 0 && (
        <div className="actions">
          <button type="button" className="button secondary" onClick={() => setPicking(true)}>
            + Meisterschaft wählen
          </button>
        </div>
      )}
      {picking && (
        <MasteryOfferPicker
          title={`Meisterschaft (Schwelle ${props.level})`}
          offers={view.moduleMasteryOffers[props.step] ?? []}
          intro="Für welche Fertigkeit? Zur Wahl steht jede Fertigkeit; ihre Mindestpunkte braucht sie erst am Ende der Erschaffung (GRW S. 23–24)."
          empty="Keine Fertigkeit verfügbar."
          onClose={() => setPicking(false)}
          onPick={(mastery, skill) => void apply({ type: "sm.addMastery", payload: { step: props.step, id: mastery.id, skill: skill.id } })}
        />
      )}
    </div>
  );
}

/** The complete free editing of a module step: skills, resources, strengths and masteries within the step's limits. */
export function AllocationEditor(props: Omit<AllocationProps, "budget">) {
  const { view } = useSm();
  const limits = view.catalog?.steps[props.step];
  const budgets = view.allocationBudgets[props.step] ?? {};
  if (!limits) return null;
  return (
    <div className="stack">
      {limits.strengthPoints > 0 && (
        <section>
          <ListHeading>Stärken</ListHeading>
          <StrengthAllocation step={props.step} allocation={props.allocation} printed={props.printed} budget={budgets.strengths ?? missingBudget} />
        </section>
      )}
      {limits.resourcePoints > 0 && (
        <section>
          <ListHeading>Ressourcen</ListHeading>
          <ResourceAllocation step={props.step} allocation={props.allocation} printed={props.printed} budget={budgets.resources ?? missingBudget} />
        </section>
      )}
      <section>
        <ListHeading>Fertigkeiten</ListHeading>
        <SkillAllocation
          step={props.step}
          allocation={props.allocation}
          printed={props.printed}
          budget={budgets.skills ?? missingBudget}
          perSkillMax={limits.perSkillMax}
          magicMax={limits.magicSchoolMaxPoints}
          magicCount={limits.magicSchoolMaxCount}
        />
      </section>
      {limits.masteries > 0 && (
        <section>
          <ListHeading>Meisterschaften</ListHeading>
          <MasteryAllocation step={props.step} allocation={props.allocation} printed={props.printed} budget={budgets.masteries ?? missingBudget} level={limits.masteryLevel} />
        </section>
      )}
    </div>
  );
}

export type { EntryDetails };
